/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

export type WaiverType = 'components' | 'containers';
export type WaiverView = 'existing' | 'requested';
export type WaiverFilterId = 'status' | 'threat' | 'policy' | 'repository';

export interface WaiverRow {
  id: string;
  status: string;
  createTime: number | null;
  expiryTime: number | null;
  scopeOwnerType: string | null;
  scopeOwnerId: string | null;
  scopeOwnerName: string | null;
  policyId: string | null;
  policyName: string | null;
  threatLevel: number;
  displayName: string | null;
  reasonText: string | null;
  comment: string | null;
  componentUpgradeAvailable: boolean | null;
  repositoryId: string | null;
  repositoryName: string | null;
  requestedBy: string | null;
  /** Container waivers: the image the waiver covers, and how many policies and components it spans. */
  containerImageId?: string | null;
  uniquePolicyCount?: number | null;
  uniqueComponentCount?: number | null;
  /** This instance's own waiver (manager or one of its repositories). Inherited waivers are read-only. */
  actionable: boolean;
}

export interface WaiverListResponse {
  total: number;
  results: WaiverRow[];
  counts: Partial<Record<WaiverFilterId, Record<string, number>>>;
}

/** Everything the list depends on. It lives in the URL, so a reload or a shared link shows the same list. */
export interface WaiversListState {
  type: WaiverType;
  view: WaiverView;
  status: string[];
  threat: string[];
  policy: string[];
  repository: string[];
  q: string;
  sort: string | null;
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const DEFAULT_PAGE_SIZE = 20;

/** A violated policy constraint and why; IQ may not name it. */
export interface WaiverConstraint {
  name: string | null;
  reasons: string[];
}

/** GET internal/ui/firewall/waiver/{waiverId}. Inherited waivers carry only what the list row has. */
export interface WaiverDetail {
  id: string;
  status: string;
  actionable: boolean;
  threatLevel: number;
  displayName: string | null;
  version: string | null;
  policyId: string | null;
  policyName: string | null;
  constraints: WaiverConstraint[];
  scopeOwnerType: string | null;
  scopeOwnerName: string | null;
  repositoryName: string | null;
  reasonText: string | null;
  comment: string | null;
  createTime: number | null;
  creatorName: string | null;
  expiryTime: number | null;
  lastRenewedAt: number | null;
  lastRenewedBy: string | null;
  lastRenewalReason: string | null;
  /** Container waivers only. */
  containerImageId?: string | null;
  uniquePolicyCount?: number | null;
  uniqueComponentCount?: number | null;
}

/** POST internal/ui/firewall/waiver/revoke. One failure does not stop the others. */
export interface RevokeResult {
  revoked: number;
  failed: { id: string; message: string }[];
}
